import { useState } from "react";
import { Icon } from "@/components/ui/Icon/Icon";
import styles from "./TagInput.module.css";

interface TagInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  id?: string;
}

/**
 * Chip-style editor for a list of short strings (voice adjectives,
 * banned terms, etc.). Enter or comma commits the current text as a
 * chip; Backspace on an empty field removes the last chip.
 */
export function TagInput({ value, onChange, placeholder, id }: TagInputProps) {
  const [draft, setDraft] = useState("");

  function commit(raw: string) {
    const t = raw.trim().replace(/,+$/, "").trim();
    if (!t) return;
    if (!value.includes(t)) onChange([...value, t]);
    setDraft("");
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && !draft && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  return (
    <div className={styles.wrap}>
      {value.map((tag) => (
        <span key={tag} className={styles.chip}>
          {tag}
          <button
            type="button"
            className={styles.remove}
            onClick={() => onChange(value.filter((t) => t !== tag))}
            aria-label={`Remove ${tag}`}
          >
            <Icon name="minus" size={11} strokeWidth={3} />
          </button>
        </span>
      ))}
      <input
        id={id}
        className={styles.input}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => commit(draft)}
        placeholder={value.length === 0 ? placeholder : ""}
      />
    </div>
  );
}
